// Vietnamese -> English for the data cells of an english quote (building,
// address, structure, fees, ...), following the wording of the customer's
// "BAO GIA TONG HOP (E).xlsx": "180 192 Nguyễn Công Trứ, Quận 1" ->
// "180 192 Nguyen Cong Tru, District 1", "3 Hầm, 25 Tầng, 6 Thang máy" ->
// "3 Basements, 25 Floors, 6 Lifts", "3 Tháng" -> "3 Months", "Quý" ->
// "Quarterly", "15 - 20 ngày" -> "15 - 20 days".
//
// Rule based, no external service: names and addresses are proper nouns and
// only lose their accents; in the other fields every known phrase is
// translated and unknown words are left as typed (accents included), so
// hasVietnamese() can point the sales person at the cells to fix by hand.

const LETTER = "\\p{L}";

// Vietnamese-only letters (the accented ones and đ)
const VIETNAMESE_RE =
  /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i;

export const hasVietnamese = (value) =>
  typeof value === "string" && VIETNAMESE_RE.test(value.normalize("NFC"));

export const stripDiacritics = (value) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .normalize("NFC");

const plural = (n, word) => (Number(n) === 1 ? word : word + "s");

const ordinal = (n) => {
  const v = Number(n) % 100;
  if (v >= 11 && v <= 13) return n + "th";
  return n + ({ 1: "st", 2: "nd", 3: "rd" }[Number(n) % 10] || "th");
};

// Whole-word, case-insensitive pattern ("\b" does not work on accented letters)
const word = (source) =>
  new RegExp(`(^|[^${LETTER}])(?:${source})(?![${LETTER}])`, "giu");

// [pattern, replacement]: patterns are matched as whole words, the
// replacement receives the capture groups of the pattern (after the prefix).
// Longer phrases come first.
const NUMBER = "(\\d+(?:[.,]\\d+)?)";
const TEXT_RULES = [
  // structure
  ["tầng trệt|trệt", () => "Ground Floor"],
  [`(\\d+)\\s*tầng\\s*hầm`, (n) => `${n} ${plural(n, "Basement")}`],
  [`(\\d+)\\s*tầng\\s*lửng`, (n) => `${n} ${plural(n, "Mezzanine")}`],
  ["tầng hầm", () => "Basement"],
  ["tầng lửng", () => "Mezzanine"],
  [`tầng\\s*(\\d+)`, (n) => ordinal(n) + " Floor"],
  [`(\\d+)\\s*hầm`, (n) => `${n} ${plural(n, "Basement")}`],
  [`(\\d+)\\s*lửng`, (n) => `${n} ${plural(n, "Mezzanine")}`],
  [`(\\d+)\\s*tầng\\s*nổi`, (n) => `${n} ${plural(n, "Floor")} above ground`],
  [`(\\d+)\\s*tầng`, (n) => `${n} ${plural(n, "Floor")}`],
  [
    `(\\d+)\\s*thang\\s*(?:máy\\s*)?(?:hàng|tải)`,
    (n) => `${n} ${plural(n, "Service Lift")}`,
  ],
  [`(\\d+)\\s*thang\\s*máy`, (n) => `${n} ${plural(n, "Lift")}`],
  [`(\\d+)\\s*thang\\s*cuốn`, (n) => `${n} ${plural(n, "Escalator")}`],
  [`(\\d+)\\s*thang\\s*bộ`, (n) => `${n} ${plural(n, "Staircase")}`],
  ["thang\\s*cuốn", () => "Escalator"],
  ["thang\\s*bộ", () => "Staircase"],
  ["thang\\s*(?:máy\\s*)?(?:hàng|tải)", () => "Service Lift"],
  ["thang\\s*máy", () => "Lift"],
  ["tầng", () => "Floor"],
  ["sàn", () => "floor"],
  ["hầm", () => "Basement"],
  // rent conditions
  ["thỏa\\s*thuận|thoả\\s*thuận|thương\\s*lượng", () => "Negotiable"],
  ["miễn\\s*phí", () => "Free"],
  ["đã\\s*bao\\s*gồm|bao\\s*gồm", () => "Included"],
  ["đang\\s*cập\\s*nhật|cập\\s*nhật", () => "To be updated"],
  // "Free tối đa 22:00" / "Free đến 21:00" -> "Free until 22:00"
  ["(?:tối\\s*đa|đến)(?=\\s*\\d{1,2}\\s*[:h])", () => "until"],
  ["tối\\s*đa", () => "up to"],
  ["đến", () => "to"],
  ["từ", () => "from"],
  ["đang\\s*trống|trống", () => "Now"],
  ["liên\\s*hệ", () => "Contact"],
  ["theo\\s*quý|hàng\\s*quý|quý", () => "Quarterly"],
  ["theo\\s*tháng|hàng\\s*tháng", () => "Monthly"],
  ["theo\\s*năm|hàng\\s*năm", () => "Yearly"],
  [`(\\d+)\\s*tháng`, (n) => `${n} ${plural(n, "Month")}`],
  [`(\\d+)\\s*năm`, (n) => `${n} ${plural(n, "Year")}`],
  [`(\\d+)\\s*tuần`, (n) => `${n} ${plural(n, "week")}`],
  [`(\\d+)\\s*ngày`, (n) => `${n} ${plural(n, "day")}`],
  [`(\\d+)\\s*giờ`, (n) => `${n} ${plural(n, "hour")}`],
  [`${NUMBER}\\s*(?:tr|triệu)`, (n) => `${n} million`],
  // parking / overtime
  // any tone on the a of "ngoài": "Gửi bãi ngoãi" is a typo found in production
  ["(?:gửi|gởi)?\\s*bãi\\s*ngo[aàáảãạ]i", () => "Outside parking"],
  ["gửi\\s*xe|bãi\\s*xe", () => "Parking"],
  ["xe\\s*máy", () => "motorbike"],
  ["ô\\s*tô|oto", () => "car"],
  ["xe", () => "vehicle"],
  ["ngoài\\s*giờ", () => "overtime"],
  ["điều\\s*hòa|điều\\s*hoà|máy\\s*lạnh", () => "air-con"],
  ["có\\s*ac", () => "With AC"],
  ["không\\s*ac", () => "Without AC"],
  ["không\\s*có", () => "None"],
  ["không", () => "No"],
  ["giờ", () => "h"],
  ["tháng", () => "month"],
  ["ngày", () => "day"],
  ["năm", () => "year"],
  ["vnđ|vnd|đồng|đ", () => "vnd"],
  ["và", () => "and"],
  ["hoặc", () => "or"],
  // rank
  ["giá\\s*rẻ", () => "Budget"],
  ["trọn\\s*gói", () => "All-inclusive"],
  ["nguyên\\s*căn", () => "Whole building"],
  ["fit-?out\\s*cơ\\s*bản", () => "basic fit-out"],
  ["cơ\\s*bản", () => "basic"],
].map(([source, replace]) => [word(source), replace]);

const applyRules = (text, rules) =>
  rules.reduce(
    (out, [re, replace]) =>
      out.replace(re, (...m) => {
        const groups = m.slice(2, -2).filter((g) => typeof g === "string");
        return m[1] + replace(...groups);
      }),
    text
  );

// "1.870.000vnđ" -> "1,870,000vnđ": a dot between groups of three digits is
// a vietnamese thousands separator, an english reader would see a decimal.
const englishThousands = (text) =>
  text.replace(/\d{1,3}(?:\.\d{3})+(?![\d.])/g, (n) => n.replace(/\./g, ","));

const tidy = (text) =>
  text
    // "AC:100" / "AC :100" -> "AC: 100", times like "8:00" stay as they are
    .replace(/(\p{L})\s*:[ \t]*(?=\S)/gu, "$1: ")
    // "180.000d/ xe": a bare d after a price is đồng
    .replace(/(\d)\s*d(?=[\s/)]|$)/gu, "$1 vnd")
    // "100,000vnd" -> "100,000 vnd", as in the customer's file
    .replace(/(\d)(vnd|usd)(?![\p{L}])/giu, "$1 $2")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

// Free text of a data cell (structure, fees, deposit, ...). Not gated on
// accents: some words are plain ascii ("12m2/Xe", "4tr").
export function translateText(value, rules = TEXT_RULES) {
  if (typeof value !== "string" || !value.trim()) return value;
  return tidy(applyRules(englishThousands(value.normalize("NFC")), rules));
}

// PAYMENT cell: a bare period is a frequency ("Quý/Tháng" ->
// "Quarterly/Monthly"), a counted one stays a duration ("3 tháng").
const PAYMENT_RULES = [
  [`(\\d+)\\s*tháng`, (n) => `${n} ${plural(n, "Month")}`],
  ["tháng", () => "Monthly"],
  ["năm", () => "Yearly"],
]
  .map(([source, replace]) => [word(source), replace])
  .concat(TEXT_RULES);

export const translatePayment = (value) => translateText(value, PAYMENT_RULES);

// One part of an address: "Phường Bến Nghé" -> "Ben Nghe Ward",
// "Quận 1" -> "District 1", "Thành phố Thủ Đức" -> "Thu Duc City"
const ADDRESS_UNITS = [
  [/^(?:phường|p\.)\s*(.+)$/iu, "Ward"],
  [/^(?:quận|q\.)\s*(.+)$/iu, "District"],
  [/^huyện\s+(.+)$/iu, "District"],
  [/^(?:thành\s*phố|tp\.?)\s*(.+)$/iu, "City"],
  [/^tỉnh\s+(.+)$/iu, "Province"],
  [/^xã\s+(.+)$/iu, "Commune"],
  [/^thị\s*trấn\s+(.+)$/iu, "Town"],
  [/^đường\s+(.+)$/iu, "Street"],
  [/^(?:khu\s*dân\s*cư|kdc)\s+(.+)$/iu, "Residential Area"],
];

function translateAddressPart(part) {
  const trimmed = part.trim();
  for (const [re, unit] of ADDRESS_UNITS) {
    const m = trimmed.match(re);
    if (!m) continue;
    const name = m[1].trim();
    // numbered units read the other way round: "Ward 12", "Street 9A"
    return /^\d/.test(name) ? `${unit} ${name}` : `${name} ${unit}`;
  }
  return trimmed;
}

export function translateAddress(value) {
  if (typeof value !== "string" || !value.trim()) return value;
  return stripDiacritics(
    value
      .normalize("NFC")
      .split(",")
      .map(translateAddressPart)
      .filter(Boolean)
      .join(", ")
  );
}

// Building names are proper nouns: "Vinatex Nguyễn Huệ" -> "Vinatex Nguyen Hue"
export const translateName = (value) =>
  typeof value === "string" ? stripDiacritics(value) : value;

const TEXT_FIELDS = {
  product_detail: ["structure"],
  rent_detail: [
    "basic_rent_cost",
    "service_cost",
    "unit_rent_and_service",
    "total_usd",
    "total_vnd",
    "over_time_cost",
    "rent_deposit",
    "motor_park_cost",
    "car_park_cost",
    "decor_time",
  ],
};

// English copy of a product as shown in the quote; the input is not touched
export function translateProduct(product) {
  const copy = JSON.parse(JSON.stringify(product));
  copy.name = translateName(copy.name);
  copy.rank = translateText(copy.rank);
  if (copy.address) {
    ["detail", "ward", "district"].forEach((field) => {
      copy.address[field] = translateAddress(copy.address[field]);
    });
  }
  if (copy.rent_detail) {
    copy.rent_detail.rent_area = translatePayment(copy.rent_detail.rent_area);
  }
  Object.entries(TEXT_FIELDS).forEach(([group, fields]) => {
    if (!copy[group]) return;
    fields.forEach((field) => {
      copy[group][field] = translateText(copy[group][field]);
    });
  });
  return copy;
}
